<script setup lang="ts">
import { useFileImportStore } from "@/stores"
import { ImportTargetType } from "@/types"
import { captureError, useEngineModel } from "@/utils/useEngineModel"
import {
  KDropdown,
  KSelectWrapper,
} from "@kaleidoscope-solutions/ks-design-system"
import { BlockIcon
} from "@kaleidoscope-solutions/ks-icons/vue/light"
import { onMounted, ref, watch } from "vue"

const model = useEngineModel()
const fileImportStore = useFileImportStore()

const importTargets = ref<{ datasets: string[]; categories: string[] }>({
  datasets: [],
  categories: []
})

const transformedTargets = ref<string[]>([])
const targetKeyTypes = ref<string[]>([])

const localTarget = ref<string>()
const additionalTarget = ref<string>()

const dropdownSelected = ref()

onMounted(async () => {
  try {
    importTargets.value = await model.getImportTargets()
    localTarget.value = fileImportStore?.targets?.[0]
    additionalTarget.value =
      // Set the additional target, not there is only one target set it to the first target.
      fileImportStore?.targets?.[1]
        ? fileImportStore?.targets?.[1]
        : fileImportStore?.targets?.[0]
  } catch (error) {
    captureError(error)
  }
})

async function getImportTargetItems(datasetNames: string[], targetKeyTypes: string[]): Promise<string[]> {
  const datasets = await Promise.all(datasetNames.map(async (datasetName) => {
    const dataset = await model.api.dataset(datasetName)
    return {
      name: dataset.name,
      keyTypes: dataset.keyTypes
    }
  }))

  return datasets.filter((dataset) => {
    return dataset.keyTypes.sort().toString() === targetKeyTypes.sort().toString()
  }).map((dataset) => dataset.name)
}

watch(localTarget, async (newLocalTarget) => {
  try {
    if (newLocalTarget !== undefined) {
      targetKeyTypes.value = (await model.api.dataset(newLocalTarget)).keyTypes
    } else {
      targetKeyTypes.value = []
    }

    transformedTargets.value = await getImportTargetItems(importTargets.value.datasets, targetKeyTypes.value)

    if (additionalTarget.value === undefined && transformedTargets.value.length === 1) {
      setAdditionalTarget(transformedTargets.value[0])
    }
  } catch (error) {
    captureError(error)
  }
})

function setLocalTarget(target: string) {
  setTarget([])

  localTarget.value = target

  additionalTarget.value = undefined
}

function setAdditionalTarget(target: string) {
  additionalTarget.value = target

  setTarget([...new Set([localTarget.value, additionalTarget.value].filter(Boolean))])
}

function setTarget(targets: string[]) {
  // setTimeout is used here to allow the dropdown selecting finish before
  // `setTargetByType` starts to process and blocks rendering.
  setTimeout(async () => {
    await fileImportStore.setTargetByType(
      targets.map((target) => target),
      ImportTargetType.Dataset
    )
  }, 0)
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <label>
        <strong class="font-bold">Upload</strong>
      </label>

      <KDropdown
        :model-value="localTarget"
        placeholder="Find"
        :items="importTargets?.datasets ?? []"
        :nullable="false"
        :multiple="false"
        class="w-52 bg-white"
        @update:model-value="setLocalTarget"
        @focusin="dropdownSelected = true"
        @focusout="dropdownSelected = false"
      >
        <template #selected="{ open }">
          <div class="w-full">
            <KSelectWrapper
              :open="open"
              :is-selected="dropdownSelected"
              class="w-full"
              data-test="import-target-dropdown-btn"
            >
              <span v-if="localTarget">{{ localTarget }}</span>
              <span
                v-else
                class="text-gray-700"
              >
                Please select
              </span>
            </KSelectWrapper>
          </div>
        </template>
        <template #item="{ item }">
          <div class="flex w-full items-center gap-1">
            <BlockIcon class="size-5 shrink-0" />
            <span>{{ item.label }}</span>
          </div>
        </template>
      </KDropdown>
    </div>

    <div v-if="localTarget && transformedTargets.length > 1">
      <label>
        <strong class="font-bold">Additional import target</strong>
      </label>

      <KDropdown
        :model-value="additionalTarget"
        placeholder="Find"
        :items="transformedTargets"
        :nullable="false"
        :multiple="false"
        class="w-52 bg-white"
        @update:model-value="setAdditionalTarget"
        @focusin="dropdownSelected = true"
        @focusout="dropdownSelected = false"
      >
        <template #selected="{ open }">
          <div class="w-full">
            <KSelectWrapper
              :open="open"
              :is-selected="dropdownSelected"

              class="w-full"
            >
              <span v-if="additionalTarget === localTarget">{{ additionalTarget }} only</span>
              <span v-else-if="additionalTarget">{{ additionalTarget }}</span>
              <span
                v-else
                class="text-gray-700"
              >
                Please select
              </span>
            </KSelectWrapper>
          </div>
        </template>
        <template #item="{ item }">
          <div class="flex w-full items-center gap-1">
            <BlockIcon class="size-5 shrink-0" />
            <span v-if="localTarget === item.label">{{ item.label }} only</span>
            <span v-else>{{ item.label }}</span>
          </div>
        </template>
      </KDropdown>
    </div>
  </div>
</template>
